import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import { userService, PurchaseHistoryItem } from "../../services/userService";
import { FiShoppingBag, FiCalendar, FiCheck, FiClock, FiX, FiChevronRight, FiPackage, FiDollarSign } from "react-icons/fi";

const PurchaseHistory: React.FC = () => {
	const { isAuthenticated } = useAuth();
	const [purchaseHistory, setPurchaseHistory] = useState<PurchaseHistoryItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

	useEffect(() => {
		const fetchPurchaseHistory = async () => {
			if (!isAuthenticated) return;

			try {
				const historyData = await userService.getPurchaseHistory();
				setPurchaseHistory(historyData);
				setLoading(false);
			} catch (error) {
				console.error("Error fetching purchase history:", error);
				toast.error("購入履歴の取得に失敗しました");
				setLoading(false);
			}
		};

		fetchPurchaseHistory();
	}, [isAuthenticated]);

	const toggleExpanded = (id: number) => {
		const newExpanded = new Set(expandedItems);
		if (newExpanded.has(id)) {
			newExpanded.delete(id);
		} else {
			newExpanded.add(id);
		}
		setExpandedItems(newExpanded);
	};

	if (loading) {
		return (
			<div className="container mx-auto max-w-5xl p-6 min-h-screen">
				<div className="flex items-center justify-center min-h-[60vh]">
					<div className="text-center">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
						<p className="mt-4 text-gray-600 dark:text-gray-400">購入履歴を読み込み中...</p>
					</div>
				</div>
			</div>
		);
	}

	if (purchaseHistory.length === 0) {
		return (
			<div className="container mx-auto max-w-5xl p-6 min-h-screen">
				<div className="space-y-6">
					{/* Header */}
					<div className="flex justify-between items-center">
						<div>
							<h2 className="text-2xl font-bold text-gray-900 dark:text-white">購入履歴</h2>
							<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">これまでの購入記録</p>
						</div>
					</div>

					{/* Empty State */}
					<div className="text-center py-10 bg-gray-50 dark:bg-gray-800 rounded-lg">
						<div className="p-6 bg-gray-100 dark:bg-gray-700 rounded-full mb-6 inline-block">
							<FiShoppingBag className="h-12 w-12 text-gray-400 dark:text-gray-500" />
						</div>
						<p className="text-gray-500 dark:text-gray-400">購入履歴はありません</p>
					</div>
				</div>
			</div>
		);
	}

	// 状態に応じたバッジの情報を返す関数
	const getStatusInfo = (status: string) => {
		const statusMap: Record<string, {
			icon: React.ReactNode;
			bgClass: string;
			textClass: string;
			label: string;
		}> = {
			completed: {
				icon: <FiCheck className="h-3.5 w-3.5" />,
				bgClass: "bg-green-100 dark:bg-green-900/50",
				textClass: "text-green-800 dark:text-green-400",
				label: "完了",
			},
			pending: {
				icon: <FiClock className="h-3.5 w-3.5" />,
				bgClass: "bg-yellow-100 dark:bg-yellow-900/50",
				textClass: "text-yellow-800 dark:text-yellow-400",
				label: "処理中",
			},
			canceled: {
				icon: <FiX className="h-3.5 w-3.5" />,
				bgClass: "bg-red-100 dark:bg-red-900/50",
				textClass: "text-red-800 dark:text-red-400",
				label: "キャンセル",
			},
		};

		return statusMap[status] || statusMap.pending;
	};

	return (
		<div className="container mx-auto max-w-5xl p-6 min-h-screen">
			<div className="space-y-6">
				{/* Header */}
				<div className="flex justify-between items-center">
					<div>
						<h2 className="text-2xl font-bold text-gray-900 dark:text-white">購入履歴</h2>
						<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">これまでの購入記録</p>
					</div>

					{/* Summary Stats */}
					<div className="hidden lg:flex space-x-6">
						<div className="text-right">
							<p className="text-sm text-gray-600 dark:text-gray-400">総購入数</p>
							<p className="text-2xl font-bold text-gray-900 dark:text-white">
								{purchaseHistory.length}
							</p>
						</div>
						<div className="text-right">
							<p className="text-sm text-gray-600 dark:text-gray-400">総額</p>
							<p className="text-2xl font-bold text-gray-900 dark:text-white">
								¥{purchaseHistory.reduce((sum, p) => sum + p.totalAmount, 0).toLocaleString()}
							</p>
						</div>
					</div>
				</div>

				{/* Purchase History List */}
				<div className="space-y-4">
					{purchaseHistory.map((purchase) => {
						const statusInfo = getStatusInfo(purchase.status);
						const isExpanded = expandedItems.has(purchase.id);

						return (
							<div
								key={purchase.id}
								className="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 dark:border-gray-700 overflow-hidden"
							>
								{/* Main Content */}
								<div className="p-6">
									<div className="flex items-start justify-between mb-4">
										<div className="flex-1">
											<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
												{purchase.eventTitle}
											</h3>
											<div className="flex flex-wrap items-center gap-4 text-sm">
												<div className="flex items-center text-gray-600 dark:text-gray-400">
													<FiCalendar className="h-4 w-4 mr-1.5" />
													<span>購入日: {purchase.purchaseDate}</span>
												</div>
												<div className="flex items-center text-gray-900 dark:text-white font-semibold">
													<FiDollarSign className="h-4 w-4 mr-1.5 text-blue-500" />
													<span>¥{purchase.totalAmount.toLocaleString()}</span>
												</div>
											</div>
										</div>

										{/* Status Badge */}
										<div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${statusInfo.bgClass} ${statusInfo.textClass}`}>
											{statusInfo.icon}
											<span className="ml-1.5">{statusInfo.label}</span>
										</div>
									</div>

									{/* Quick Summary */}
									<div className="flex items-center justify-between">
										<div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
											<FiPackage className="h-4 w-4 mr-1.5" />
											<span>
												{purchase.items.reduce((sum, item) => sum + item.quantity, 0)}枚のチケット
											</span>
										</div>

										{/* Expand/Collapse Button */}
										<button
											onClick={() => toggleExpanded(purchase.id)}
											className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
										>
											<span>{isExpanded ? '詳細を隠す' : '詳細を見る'}</span>
											<FiChevronRight className={`h-4 w-4 ml-1 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
										</button>
									</div>

									{/* Expanded Details */}
									{isExpanded && (
										<div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
											<h4 className="font-medium text-gray-900 dark:text-white mb-4">購入内容</h4>
											<div className="space-y-3">
												{purchase.items.map((item, index) => (
													<div
														key={index}
														className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
													>
														<div className="flex items-center space-x-3">
															<div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded">
																<FiPackage className="h-4 w-4 text-blue-600 dark:text-blue-400" />
															</div>
															<div>
																<p className="font-medium text-gray-900 dark:text-white">
																	{item.ticketTypeName}
																</p>
																<p className="text-sm text-gray-600 dark:text-gray-400">
																	数量: {item.quantity}
																</p>
															</div>
														</div>
														<span className="font-semibold text-gray-900 dark:text-white">
															¥{item.subtotal.toLocaleString()}
														</span>
													</div>
												))}
											</div>

											{/* Total Summary */}
											<div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
												<div className="flex justify-between items-center">
													<span className="text-lg font-semibold text-gray-900 dark:text-white">
														合計
													</span>
													<span className="text-xl font-bold text-blue-600 dark:text-blue-400">
														¥{purchase.totalAmount.toLocaleString()}
													</span>
												</div>
											</div>
										</div>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default PurchaseHistory;